// src/pages/Membership.jsx
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Stack,
    CircularProgress,
    Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import CloseIcon from '@mui/icons-material/Close';
import membershipImg from '../assets/membership/membership.JPG';
import MembershipForm from '../components/Members/MembershipForm';

const MEMBERSHIP_PRICES = { single: 25, couple: 45, family: 60 };
const MEMBERSHIP_LABELS = {
    single: 'Single Membership',
    couple: 'Couple Membership',
    family: 'Family Membership',
};

export default function Membership() {
    const [open, setOpen] = useState(false);

    // Wizard: 1 = info, 2 = payment, 3 = complete
    const [step, setStep] = useState(1);

    // Selection: membership type + payment method
    const [membershipType, setMembershipType] = useState('single'); // default
    const price = MEMBERSHIP_PRICES[membershipType];
    const planLabel = MEMBERSHIP_LABELS[membershipType];

    const [method, setMethod] = useState(''); // '', 'paypal', 'check'
    const [payPalBusy, setPayPalBusy] = useState(false);
    const [paypalError, setPaypalError] = useState('');
    const [paypalOrderId, setPaypalOrderId] = useState('');

    // Submission progress (Step 3)
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState('');

    const FORM_ID = 'membership-form';

    const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
    const PAYPAL_AVAILABLE = Boolean(PAYPAL_CLIENT_ID);

    const resetDialog = () => {
        setStep(1);
        setMembershipType('single');
        setMethod('');
        setPayPalBusy(false);
        setPaypalError('');
        setPaypalOrderId('');
        setSubmitting(false);
        setSubmitted(false);
        setSubmissionError('');
    };

    const handleOpen = () => {
        resetDialog();
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const validateStep1 = () => {
        const formEl = document.getElementById(FORM_ID);
        if (!formEl) return false;
        if (typeof formEl.reportValidity === 'function') return formEl.reportValidity();
        return true;
    };

    const goToPayment = () => {
        if (validateStep1()) setStep(2);
    };

    const requestFormSubmit = () => {
        const formEl = document.getElementById(FORM_ID);
        if (!formEl) return;
        if (formEl.requestSubmit) formEl.requestSubmit();
        else formEl.submit();
    };

    // Submit by CHECK
    const submitForCheck = () => {
        setMethod('check');
        setSubmitting(true);
        // show "Submitting…" on button briefly; then advance to step 3
        setTimeout(() => {
            setStep(3);
            requestFormSubmit();
        }, 0);
    };

    return (
        <>
            <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
                {/* Intro */}
                <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 }, px: { xs: 2, md: 0 } }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.35rem', sm: '1.5rem' },
                            lineHeight: { xs: 1.3 },
                        }}
                    >
                        Join Our Community
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'left',
                            maxWidth: 800,
                            mx: 'auto',
                            fontSize: { xs: '1rem' },
                            lineHeight: { xs: 1.7, md: 1.75 },
                        }}
                    >
                        Become a member of the Piedmont Historical Society and connect with fellow history lovers!
                        We’d love to have you on board preserving and celebrating our local and state heritage.
                    </Typography>
                </Box>

                {/* Image + CTA */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, sm: 4, md: 6 },
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        px: { xs: 2, md: 0 },
                    }}
                >
                    <Box
                        component="img"
                        src={membershipImg}
                        alt="Become a Member"
                        sx={{
                            width: { xs: '100%', md: '45%' },
                            height: 'auto',
                            borderRadius: { xs: 2, md: 2 },
                            boxShadow: { xs: 1, md: 0 },
                        }}
                    />

                    <Box sx={{ width: { xs: '100%', md: '55%' } }}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: { xs: 2.5, md: 3 },
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 1.5, md: 2 },
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem' } }}>
                                Membership Benefits
                            </Typography>

                            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 28 }}>
                                    <li>Educational lectures every two months (6 per year)</li>
                                    <li>Access to activities at the Depot</li>
                                    <li>Access to Robert's Home museum when not in use</li>
                                    <li>Free admission during regular Depot hours</li>
                                </ul>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Pricing:</strong> Single — <strong>$25</strong>, Couple — <strong>$45</strong>, Family — <strong>$60</strong>.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Want these perks for free? <Link component={RouterLink} to="/volunteer">Sign up as a Volunteer</Link>!
                                </Typography>
                            </Alert>

                            <Divider sx={{ my: 1 }} />

                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem' } }}>
                                Ready to join? We’d love to have you.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click below to fill out your membership application.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleOpen}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Become a Member
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Container>

            {/* Popup Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, md: 2 },
                    },
                }}
            >
                <DialogTitle sx={{ pr: 6, py: { xs: 1.25, md: 2 } }}>
                    Membership Application
                    <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        bgcolor: 'background.default',
                        px: { xs: 2, md: 3 },
                        '& .membership-form-embed .MuiPaper-root, & .membership-form-embed .MuiCard-root': {
                            boxShadow: 'none',
                            border: 'none',
                            background: 'transparent',
                        },
                        '& .membership-form-embed form [type="submit"]': {
                            display: 'none',
                        },
                    }}
                >
                    <Stepper activeStep={step - 1} sx={{ mb: { xs: 2, md: 3 } }}>
                        <Step><StepLabel>Member Info</StepLabel></Step>
                        <Step><StepLabel>Payment (Required)</StepLabel></Step>
                        <Step><StepLabel>Complete</StepLabel></Step>
                    </Stepper>

                    {/* STEP 1: Member Info */}
                    {step === 1 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.05rem' } }}>
                                Member Information
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Please choose your membership type and provide your contact details. Fields marked with * are required.
                            </Typography>
                        </Box>
                    )}

                    {/* Keep the form mounted across steps so we can submit from Step 2/3 */}
                    <Box className="membership-form-embed" sx={{ display: step === 1 ? 'block' : 'none' }}>
                        <MembershipForm
                            formId={FORM_ID}
                            disabled={step !== 1 || submitting}
                            paymentMethod={method}
                            paypalOrderId={paypalOrderId}
                            membershipType={membershipType}
                            onMembershipTypeChange={setMembershipType}
                            onSuccess={() => {
                                setSubmitting(false);
                                setSubmitted(true);
                                setSubmissionError('');
                                setStep(3);
                            }}
                            onError={(msg) => {
                                setSubmitting(false);
                                setSubmitted(false);
                                setSubmissionError(msg || 'We could not save your application. Please try again.');
                                setStep(3);
                            }}
                        />
                    </Box>

                    {/* STEP 2: Payment */}
                    {step === 2 && (
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.05rem' } }}>
                                Payment
                            </Typography>

                            <Typography variant="body2" sx={{ mb: 2 }}>
                                You selected <strong>{planLabel}</strong>. Choose a payment method for the <strong>${price}</strong> annual dues.
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                                <Button
                                    variant={method === 'paypal' ? 'contained' : 'outlined'}
                                    onClick={() => setMethod('paypal')}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Pay with PayPal
                                </Button>
                                <Button
                                    variant={method === 'check' ? 'contained' : 'outlined'}
                                    onClick={() => setMethod('check')}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Mail a Check
                                </Button>
                            </Stack>

                            {method === '' && (
                                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                    Select a payment method to continue.
                                </Alert>
                            )}

                            {method === 'check' && (
                                <Alert severity="info" variant="outlined" sx={{ mt: 1, borderRadius: 2 }}>
                                    Please mail a <strong>${price}</strong> check to:
                                    <br />
                                    <strong>Piedmont Historical Society Museum</strong> — 130 N Center Ave, Piedmont, Alabama 36272
                                </Alert>
                            )}

                            {method === 'paypal' && (
                                <Box sx={{ mt: 1 }}>
                                    {!PAYPAL_AVAILABLE ? (
                                        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                                            PayPal is unavailable in this build (missing <code>REACT_APP_PAYPAL_CLIENT_ID</code>).
                                            Add the sandbox client ID to your environment and rebuild.
                                        </Alert>
                                    ) : (
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                Pay securely with PayPal. <em>After payment, we’ll automatically finish your application.</em>
                                            </Typography>

                                            {paypalError && (
                                                <Alert severity="error" sx={{ mb: 1 }}>
                                                    {paypalError}
                                                </Alert>
                                            )}

                                            <PayPalButtons
                                                style={{ layout: 'vertical' }}
                                                disabled={payPalBusy}
                                                onClick={(data, actions) => {
                                                    return validateStep1() ? actions.resolve() : actions.reject();
                                                }}
                                                createOrder={(data, actions) => {
                                                    setPaypalError('');
                                                    setPayPalBusy(true);
                                                    return actions.order.create({
                                                        purchase_units: [
                                                            {
                                                                description: `Piedmont Historical Society – ${planLabel}`,
                                                                amount: { value: price.toFixed(2) },
                                                            },
                                                        ],
                                                        application_context: { shipping_preference: 'NO_SHIPPING' },
                                                    });
                                                }}
                                                onApprove={async (data, actions) => {
                                                    try {
                                                        const details = await actions.order.capture();
                                                        const orderId = details?.id || '';
                                                        setMethod('paypal');
                                                        setPaypalOrderId(orderId);
                                                        setSubmitting(true);
                                                        setStep(3);
                                                        requestFormSubmit();
                                                    } catch (e) {
                                                        setPaypalError('There was a problem capturing the payment. Please try again.');
                                                    } finally {
                                                        setPayPalBusy(false);
                                                    }
                                                }}
                                                onCancel={() => setPayPalBusy(false)}
                                                onError={(err) => {
                                                    console.error('PayPal error', err);
                                                    setPaypalError('PayPal failed to initialize or was blocked. Try again or choose “Mail a Check”.');
                                                    setPayPalBusy(false);
                                                }}
                                            />
                                        </Paper>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* STEP 3: Complete */}
                    {step === 3 && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            {!submitted && !submissionError && (
                                <>
                                    <CircularProgress size={28} />
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        Finishing your application…
                                    </Typography>
                                </>
                            )}

                            {submitted && (
                                <Alert severity="success" sx={{ textAlign: 'left', borderRadius: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        You’re all set!
                                    </Typography>
                                    <Typography variant="body2">
                                        Thanks for joining the Piedmont Historical Society. We’ll email the team and follow up soon.
                                    </Typography>
                                </Alert>
                            )}

                            {submissionError && (
                                <Alert severity="error" sx={{ textAlign: 'left', borderRadius: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        We couldn’t save your application.
                                    </Typography>
                                    <Typography variant="body2">{submissionError}</Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>

                {/* Actions */}
                <DialogActions
                    sx={{
                        p: 2,
                        flexWrap: 'wrap',
                        gap: 1,
                        '> .MuiTypography-root': { flex: 1 },
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Your information is only used for membership records.
                    </Typography>

                    {step === 1 && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <Button onClick={handleClose} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                            <Button onClick={goToPayment} variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                Next
                            </Button>
                        </Stack>
                    )}

                    {step === 2 && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <Button onClick={() => setStep(1)} sx={{ width: { xs: '100%', sm: 'auto' } }}>Back</Button>
                            {/* Hide Submit for PayPal (auto-submits). Show only for "Mail a Check". */}
                            {method === 'check' && (
                                <Button
                                    variant="contained"
                                    onClick={submitForCheck}
                                    disabled={submitting}
                                    sx={{
                                        width: { xs: '100%', sm: 'auto' },
                                        '&.Mui-disabled': {
                                            color: 'common.white',
                                            bgcolor: 'primary.main',
                                            opacity: 0.75,
                                        },
                                    }}
                                >
                                    {submitting ? 'Submitting…' : 'Submit Application'}
                                </Button>
                            )}
                        </Stack>
                    )}

                    {step === 3 && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            {!submitted && !submissionError ? (
                                <Button
                                    disabled
                                    sx={{
                                        width: { xs: '100%', sm: 'auto' },
                                        '&.Mui-disabled': {
                                            color: 'common.white',
                                            bgcolor: 'primary.main',
                                            opacity: 0.75,
                                        },
                                    }}
                                >
                                    Working…
                                </Button>
                            ) : (
                                <Button variant="contained" onClick={handleClose} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                    Done
                                </Button>
                            )}
                        </Stack>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
